/**
 * Competitions that start themselves.
 *
 * Kate's competitions existed but nothing ever triggered one, so you had to know a
 * keyboard key to see the only part of the game you can actually win. How one starts is
 * an open question for her — she never said — so this is the smallest thing that makes
 * them part of play rather than a hidden feature:
 *
 *   A competition is announced, counts down, runs, and then there is a lull before the
 *   next. You are told which one is coming so you can choose to take part or ignore it.
 *
 * Logged as an assumption. When she answers, this is the one file to change.
 */
import { COMPETITION_ORDER, COMPETITIONS, type CompetitionKind } from './competitions'

/** Seconds of quiet before the first competition of a session. */
const OPENING_LULL = 70
/** Seconds between one finishing and the next being announced. */
const LULL_BETWEEN: [number, number] = [90, 150]
/** Seconds of warning before it begins. */
const COUNTDOWN = 12

export type SchedulerPhase = 'lull' | 'announced' | 'running'

export class CompetitionScheduler {
  phase: SchedulerPhase = 'lull'
  /** Seconds until the next phase change. */
  timeLeft = OPENING_LULL
  /** What is coming, or running. */
  next: CompetitionKind = COMPETITION_ORDER[0]!

  private readonly random: () => number
  /** Fired when the countdown ends and the competition should actually begin. */
  onStart: ((kind: CompetitionKind) => void) | null = null
  /** Fired when one is announced, so the player can be told. */
  onAnnounce: ((kind: CompetitionKind, seconds: number) => void) | null = null

  constructor(seed = 606) {
    let a = seed >>> 0
    this.random = () => {
      a = (a + 0x6d2b79f5) >>> 0
      let t = Math.imul(a ^ (a >>> 15), 1 | a)
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
    this.next = COMPETITION_ORDER[Math.floor(this.random() * COMPETITION_ORDER.length)]!
  }

  /** Tell the scheduler a competition has ended, so it can go back to a lull. */
  finished(): void {
    this.phase = 'lull'
    const [min, max] = LULL_BETWEEN
    this.timeLeft = min + this.random() * (max - min)
    // Never the same one twice in a row — variety with no bookkeeping.
    const others = COMPETITION_ORDER.filter((k) => k !== this.next)
    this.next = others[Math.floor(this.random() * others.length)]!
  }

  update(dt: number): void {
    if (this.phase === 'running') return
    this.timeLeft -= dt
    if (this.timeLeft > 0) return

    if (this.phase === 'lull') {
      this.phase = 'announced'
      this.timeLeft = COUNTDOWN
      this.onAnnounce?.(this.next, COUNTDOWN)
    } else {
      this.phase = 'running'
      this.onStart?.(this.next)
    }
  }

  /** Human-readable state for the HUD. */
  get label(): string {
    if (this.phase === 'running') return 'competition running'
    const name = COMPETITIONS[this.next].name
    if (this.phase === 'announced') return `${name} in ${Math.ceil(this.timeLeft)}s`
    return `${name} in ${Math.ceil(this.timeLeft / 10) * 10}s`
  }
}
